import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Controller, Control } from "react-hook-form";
import { ttsGenerateUrl } from "../../../config";

type MediaType = "image" | "audio" | "tts" | "video";

/**
 * NOTE: This is intentionally a copy of `media-upload.tsx`.
 * Edit flow needs different behavior: existing media URLs are treated as "keep as-is"
 * unless the user uploads a replacement. We keep this isolated from create/view.
 */
interface MediaUploadEditProps {
  tabIndex?: number;
  mediaType: MediaType | null | undefined;
  mediaFile: FileList | string | undefined; // existing URL/filename for current language, or newly selected
  control: Control<any>;
  title?: string;
  language?: string;
  setSharedData: (data: any) => void;
  onMediaTypeChange: (type: MediaType) => void;
  onMediaFileChange: (file: FileList | null) => void;
  onUploadSuccess: (url: string, campaignId: string, fileName?: string) => void;
  onTtsGenerated?: (language: string, file: File) => void;
  onAudioFileChange?: (language: string, file: File) => void;
  onVideoFileChange?: (language: string, file: File) => void;
  onImageFileChange?: (language: string, file: File) => void;
  // Optional "secondary" image used with Audio/TTS (notification-style)
  imageFile?: File | string | undefined;
  onImageFileChangeSecondary?: (language: string, file: File | null) => void;
  onError: (message: string) => void;
}

export const MediaUploadEdit: React.FC<MediaUploadEditProps> = ({
  tabIndex = 0,
  mediaType,
  mediaFile,
  control,
  language = "en",
  setSharedData,
  onMediaTypeChange,
  onMediaFileChange,
  onError,
  onTtsGenerated,
  onAudioFileChange,
  onVideoFileChange,
  onImageFileChange,
  imageFile,
  onImageFileChangeSecondary,
}) => {
  console.log("imageFile props>>",imageFile);
  
  const normalizedMediaType: MediaType | null = (mediaType as MediaType) || null;
  const safeLang = language.toLowerCase();

  const [mediaByLanguage, setMediaByLanguage] = useState<
    Record<
      string,
      {
        file?: File;
        url?: string;
        label?: string;
        ttsText?: string;
      }
    >
  >({});

  const [videoBlobUrls, setVideoBlobUrls] = useState<Record<string, string>>({});
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});

  const [isConvertingTts, setIsConvertingTts] = useState(false);
  const [currentTtsText, setCurrentTtsText] = useState("");
  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // existing-media preview URLs (edit-mode)
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const [showMediaTypeChangeDialog, setShowMediaTypeChangeDialog] = useState(false);
  const [pendingMediaType, setPendingMediaType] = useState<MediaType | null>(null);
  const [pendingFieldOnChange, setPendingFieldOnChange] = useState<((value: MediaType) => void) | null>(null);

  const [currentMedia, setCurrentMedia] = useState<{
    file?: File;
    url?: string;
    label?: string;
    ttsText?: string;
  }>({});

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") resolve(reader.result);
        else reject(new Error("Failed to convert file to base64"));
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleConvertToSpeech = async () => {
    if (!currentTtsText.trim()) {
      onError("Please enter text to convert to speech");
      return;
    }

    try {
      setIsConvertingTts(true);
      const response = await fetch(ttsGenerateUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: currentTtsText, language }),
      });
      if (!response.ok) throw new Error("TTS request failed");
      const data = await response.json();
      if (!data?.data_url) throw new Error("Invalid TTS response");

      const generatedUrl = data.data_url;

      setMediaByLanguage((prev) => ({
        ...prev,
        [safeLang]: {
          ...prev[safeLang],
          url: generatedUrl,
          label: `TTS Audio (${language})`,
          ttsText: currentTtsText,
        },
      }));

      const responseBlob = await fetch(generatedUrl);
      const audioBlob = await responseBlob.blob();
      const mimeType = generatedUrl.match(/data:([^;]+)/)?.[1] || "audio/wav";
      const fileExt = mimeType.includes("wav") ? "wav" : "mp3";
      const file = new File([audioBlob], `tts_${safeLang}.${fileExt}`, { type: mimeType });

      onTtsGenerated?.(safeLang, file);
    } catch (error) {
      console.error("TTS error:", error);
      onError("Failed to convert text to speech");
    } finally {
      setIsConvertingTts(false);
    }
  };

  const handleFileUpload = React.useCallback(
    async (files: FileList | null) => {
      if (!files || !files[0]) return;
      const file = files[0];

      // TTS is generated from text, not uploaded as a file
      // This should never be called when TTS is selected (button is hidden), but add safeguard
      if (normalizedMediaType === "tts") {
        onError("TTS is generated from text, not uploaded as a file. Please use 'Convert to Speech' button.");
        return;
      }

      if (normalizedMediaType === "image" || normalizedMediaType === null) {
        if (imageBlobUrls[safeLang]) URL.revokeObjectURL(imageBlobUrls[safeLang]);
        const blobUrl = URL.createObjectURL(file);
        setImageBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
        setMediaByLanguage((prev) => ({ ...prev, [safeLang]: { file, url: blobUrl, label: file.name } }));
        setCurrentMedia({ file, url: blobUrl, label: file.name });
        // Set preview URL for current language
        setPreviewImageUrl(blobUrl);
        onImageFileChange?.(safeLang, file);
      } else if (normalizedMediaType === "audio") {
        try {
          const base64Url = await fileToBase64(file);
          setMediaByLanguage((prev) => ({ ...prev, [safeLang]: { file, url: base64Url, label: file.name } }));
          setCurrentMedia((prev) => ({ ...prev, file, url: base64Url, label: file.name }));
          onAudioFileChange?.(safeLang, file);
        } catch (error) {
          console.error("Error converting audio to base64:", error);
          onError("Failed to process audio file");
          return;
        }
      } else if (normalizedMediaType === "video") {
        try {
          if (videoBlobUrls[safeLang]) URL.revokeObjectURL(videoBlobUrls[safeLang]);
          const blobUrl = URL.createObjectURL(file);
          setVideoBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
          setMediaByLanguage((prev) => ({ ...prev, [safeLang]: { file, url: blobUrl, label: file.name } }));
          setCurrentMedia({ file, url: blobUrl, label: file.name });
          onVideoFileChange?.(safeLang, file);
        } catch (error) {
          console.error("Error processing video file:", error);
          onError("Failed to process video file");
          return;
        }
      }

      onMediaFileChange(files);
    },
    [normalizedMediaType, safeLang, onMediaFileChange, onAudioFileChange, onVideoFileChange, onImageFileChange, onError, imageBlobUrls, videoBlobUrls]
  );

  // In edit mode, a string mediaFile means "existing media" unless user uploads a new file.
  useEffect(() => {
    if (!mediaFile) return;
    if (typeof mediaFile === "string") {
      const label =
        normalizedMediaType === "audio"
          ? "Existing audio"
          : normalizedMediaType === "video"
          ? "Existing video"
          : normalizedMediaType === "image"
          ? "Existing image"
          : "Existing file";

      setMediaByLanguage((prev) => ({
        ...prev,
        [safeLang]: { ...prev[safeLang], url: mediaFile, label },
      }));

      setCurrentMedia((prev) => ({ ...prev, url: mediaFile, label }));

      if (normalizedMediaType === "audio") setPreviewAudioUrl(mediaFile);
      if (normalizedMediaType === "video") setPreviewVideoUrl(mediaFile);
      if (normalizedMediaType === "image") setPreviewImageUrl(mediaFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFile, normalizedMediaType, safeLang]);

  useEffect(() => {
    if (normalizedMediaType === "tts") setCurrentTtsText(currentMedia.ttsText || "");
  }, [safeLang, normalizedMediaType, currentMedia.ttsText]);

  // Reset audio player on tab/language change for TTS/Audio
  const previousAudioUrlRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (normalizedMediaType !== "tts" && normalizedMediaType !== "audio") {
      previousAudioUrlRef.current = undefined;
      return;
    }
    const currentAudioUrl = currentMedia.url;
    if (previousAudioUrlRef.current === currentAudioUrl) return;
    previousAudioUrlRef.current = currentAudioUrl;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = currentAudioUrl || "";
      audioRef.current.load();
    }
  }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

  // Reset video player on tab/language change for Video
  const previousVideoUrlRef = useRef<string | undefined>(undefined);
  const previousLangRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (normalizedMediaType !== "video") {
      previousVideoUrlRef.current = undefined;
      previousLangRef.current = undefined;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        videoRef.current.src = "";
        videoRef.current.removeAttribute("src");
        videoRef.current.load();
      }
      return;
    }

    const currentVideoUrl = currentMedia.url;
    const langChanged = previousLangRef.current !== safeLang;
    previousLangRef.current = safeLang;
    const shouldUpdate = langChanged || previousVideoUrlRef.current !== currentVideoUrl;
    if (!shouldUpdate) return;
    previousVideoUrlRef.current = currentVideoUrl;

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      if (currentVideoUrl) {
        videoRef.current.src = currentVideoUrl;
        videoRef.current.load();
      } else {
        videoRef.current.src = "";
        videoRef.current.removeAttribute("src");
        const sources = videoRef.current.querySelectorAll("source");
        sources.forEach((source) => source.remove());
        videoRef.current.load();
      }
    }
  }, [tabIndex, safeLang, normalizedMediaType, currentMedia.url]);

  // Cleanup blob URLs only on component unmount
  useEffect(() => {
    return () => {
      Object.values(videoBlobUrls).forEach((url) => url?.startsWith("blob:") && URL.revokeObjectURL(url));
      Object.values(imageBlobUrls).forEach((url) => url?.startsWith("blob:") && URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep currentMedia synced for language switching (use stored file if replaced, else existing URL)
  useEffect(() => {
    let mediaUrl: string | undefined;
    if (normalizedMediaType === "image") {
      const storedFile = mediaByLanguage[safeLang]?.file;
      const storedUrl = mediaByLanguage[safeLang]?.url;
      // If we already have a blob URL for this language, use it
      if (imageBlobUrls[safeLang]) {
        mediaUrl = imageBlobUrls[safeLang];
      } else if (storedFile) {
        // Create new blob URL if we have a file but no blob URL yet
        const blobUrl = URL.createObjectURL(storedFile);
        setImageBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
        mediaUrl = blobUrl;
      } else if (storedUrl) {
        // Use stored URL if file was already processed
        mediaUrl = storedUrl;
      } else if (typeof mediaFile === "string") {
        mediaUrl = mediaFile;
      } else if (mediaFile instanceof FileList && mediaFile[0]) {
        const blobUrl = URL.createObjectURL(mediaFile[0]);
        setImageBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
        mediaUrl = blobUrl;
      } else {
        mediaUrl = undefined;
      }
    } else if (normalizedMediaType === "video") {
      const storedFile = mediaByLanguage[safeLang]?.file;
      if (storedFile) {
        if (videoBlobUrls[safeLang]) URL.revokeObjectURL(videoBlobUrls[safeLang]);
        const blobUrl = URL.createObjectURL(storedFile);
        setVideoBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
        mediaUrl = blobUrl;
      } else if (typeof mediaFile === "string") {
        mediaUrl = mediaFile;
      } else if (mediaFile instanceof FileList && mediaFile[0]) {
        const blobUrl = URL.createObjectURL(mediaFile[0]);
        setVideoBlobUrls((prev) => ({ ...prev, [safeLang]: blobUrl }));
        mediaUrl = blobUrl;
      } else {
        mediaUrl = undefined;
      }
    } else {
      mediaUrl = mediaByLanguage[safeLang]?.url || (typeof mediaFile === "string" ? mediaFile : undefined);
    }

    if (mediaUrl) {
      const mediaData =
        normalizedMediaType === "image"
          ? mediaByLanguage[safeLang]?.file
            ? { ...mediaByLanguage[safeLang], url: mediaUrl }
            : { url: mediaUrl, label: "Existing image" }
          : normalizedMediaType === "video"
          ? mediaByLanguage[safeLang]?.file
            ? { ...mediaByLanguage[safeLang], url: mediaUrl }
            : { url: mediaUrl, label: "Existing video" }
          : mediaByLanguage[safeLang] || { url: mediaUrl, label: "Existing file" };

      setCurrentMedia(mediaData);
      if (normalizedMediaType === "audio" || normalizedMediaType === "tts") setPreviewAudioUrl(mediaUrl);
      if (normalizedMediaType === "video") setPreviewVideoUrl(mediaUrl);
      if (normalizedMediaType === "image") setPreviewImageUrl(mediaUrl);
    } else {
      setCurrentMedia({});
      if (normalizedMediaType === "audio" || normalizedMediaType === "tts") setPreviewAudioUrl(null);
      if (normalizedMediaType === "video") setPreviewVideoUrl(null);
      if (normalizedMediaType === "image") setPreviewImageUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, normalizedMediaType, safeLang, mediaByLanguage, mediaFile]);

  const renderMediaPreview = () => {
    console.log("imageFile>>", imageFile);
    if (!normalizedMediaType) return null;

    if (normalizedMediaType === "audio" || normalizedMediaType === "tts") {
      const hasAudio = currentMedia.url || previewAudioUrl;
      const hasImage = imageFile;
      
      // Show preview if there's audio OR image (or both)
      if (hasAudio || hasImage) {
        return (
          <Box mt={2}>
            <Typography variant="subtitle2">Preview</Typography>
            {/* Audio first, then image below */}
            {hasAudio && (
              <audio 
                ref={audioRef} 
                controls 
                style={{ width: "100%", marginBottom: hasImage ? 16 : 0 }} 
                className="mt-1"
              >
                <source src={(currentMedia.url || previewAudioUrl) as string} />
              </audio>
            )}
            {/* Image below audio if exists, or standalone if no audio */}
            {hasImage && (
              <Box mt={hasAudio ? 2 : 0}>
                {typeof imageFile === 'string' ? (
                  <img
                    src={imageFile}
                    alt="Uploaded"
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                  />
                ) : imageFile instanceof File ? (
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="Uploaded"
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                  />
                ) : null}
              </Box>
            )}
          </Box>
        );
      }
    }

    if (normalizedMediaType === "video" && (currentMedia.url || previewVideoUrl)) {
      return (
        <Box mt={2}>
          <Typography variant="subtitle2">Preview</Typography>
          <video ref={videoRef} controls style={{ width: "100%", maxHeight: 280 }}>
            <source src={(currentMedia.url || previewVideoUrl) as string} />
          </video>
        </Box>
      );
    }

    if (normalizedMediaType === "image") {      
      // Check multiple sources for the image URL - prioritize per-language blob URLs
      const imageUrl = imageBlobUrls[safeLang] || 
                       mediaByLanguage[safeLang]?.url || 
                       currentMedia.url || 
                       previewImageUrl;
      console.log("imageUrl>>", imageUrl);
      if (imageUrl) {
        return (
          <Box mt={2}>
            <Typography variant="subtitle2">Preview</Typography>
            <img
              src={imageUrl}
              alt="preview"
              style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, border: "1px solid #eee" }}
            />
          </Box>
        );
      }
    }

    return null;
  };

  const handleConfirmMediaTypeChange = () => {
    if (pendingMediaType && pendingFieldOnChange) {
      // Clear secondary images first (before clearing mediaByLanguage)
      if (onImageFileChangeSecondary) {
        Object.keys(mediaByLanguage).forEach((lang) => {
          onImageFileChangeSecondary(lang, null);
        });
      }
      
      // Revoke all blob URLs
      Object.values(imageBlobUrls).forEach((url) => {
        if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
      });
      Object.values(videoBlobUrls).forEach((url) => {
        if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
      });
      
      // Clear all media state when changing media type
      setMediaByLanguage({});
      setCurrentMedia({});
      setPreviewAudioUrl(null);
      setPreviewVideoUrl(null);
      setPreviewImageUrl(null);
      setImageBlobUrls({});
      setVideoBlobUrls({});
      
      pendingFieldOnChange(pendingMediaType);
      onMediaTypeChange(pendingMediaType);
      setSharedData((prev: any) => ({ ...prev, mediaType: pendingMediaType, mediaFile: undefined }));
    }
    setShowMediaTypeChangeDialog(false);
    setPendingMediaType(null);
    setPendingFieldOnChange(null);
  };

  const handleCancelMediaTypeChange = () => {
    setShowMediaTypeChangeDialog(false);
    setPendingMediaType(null);
    setPendingFieldOnChange(null);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Controller
        name="mediaType"
        control={control}
        render={({ field }) => (
          <FormControl>
            <FormLabel>Media Type</FormLabel>
            <RadioGroup
              row
              value={field.value || ""}
              onChange={(e) => {
                const next = e.target.value as MediaType;
                if (field.value && field.value !== next) {
                  // editing: confirm before changing media type (could drop existing media)
                  setPendingMediaType(next);
                  setPendingFieldOnChange(() => field.onChange);
                  setShowMediaTypeChangeDialog(true);
                } else {
                  field.onChange(next);
                  onMediaTypeChange(next);
                }
              }}
            >
              <FormControlLabel value="image" control={<Radio />} label="Image" />
              <FormControlLabel value="tts" control={<Radio />} label="TTS" />
              <FormControlLabel value="audio" control={<Radio />} label="Audio" />
              <FormControlLabel value="video" control={<Radio />} label="Video" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {normalizedMediaType === "tts" && (
        <Box mt={2}>
          <TextField
            fullWidth
            label={`TTS Text (${language})`}
            value={currentTtsText}
            onChange={(e) => setCurrentTtsText(e.target.value)}
          />
          <Button sx={{ mt: 1 }} variant="contained" onClick={handleConvertToSpeech} disabled={isConvertingTts}>
            {isConvertingTts ? "Converting..." : "Convert to Speech"}
          </Button>
        </Box>
      )}

      {!!normalizedMediaType && normalizedMediaType !== "tts" && (
        <Box mt={2}>
          <Button variant="outlined" component="label">
            Upload {normalizedMediaType.toUpperCase()}
            <input
              type="file"
              hidden
              accept={
                normalizedMediaType === "image"
                  ? "image/*"
                  : normalizedMediaType === "audio"
                  ? "audio/*"
                  : normalizedMediaType === "video"
                  ? "video/*"
                  : "*/*"
              }
              onChange={(e) => handleFileUpload(e.target.files)}
            />
          </Button>
          {currentMedia.label && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              {currentMedia.label}
            </Typography>
          )}
        </Box>
      )}

      {/* Secondary image for Audio/TTS (per language, notification-style) */}
      {(normalizedMediaType === "audio" || normalizedMediaType === "tts") && (
        <Box mt={2}>
          {/* <Typography variant="subtitle2">Image (required for Audio/TTS)</Typography> */}
          <Button variant="outlined" component="label">
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0] && onImageFileChangeSecondary) {
                  onImageFileChangeSecondary(safeLang, e.target.files[0]);
                }
              }}
            />
          </Button>
          {typeof imageFile === "string" && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Existing image selected
            </Typography>
          )}
          {imageFile instanceof File && (
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              {imageFile.name}
            </Typography>
          )}
        </Box>
      )}

      {renderMediaPreview()}

      <Dialog open={showMediaTypeChangeDialog} onClose={handleCancelMediaTypeChange}>
        <DialogTitle>Change Media Type?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Changing media type may drop existing media selections for this content. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelMediaTypeChange}>Cancel</Button>
          <Button onClick={handleConfirmMediaTypeChange} variant="contained">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

